import { useTimeoutPoll } from "@vueuse/core";
import { onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

interface RegenerationOptions<T> {
    /** Re-read the results; the poll stops once `generatedAt(latest)` moves past the baseline. */
    fetchResults: () => Promise<T | null>;
    generatedAt: (results: T | null) => string | null;
    /** POST the regeneration request; resolve `false` when it was not queued. */
    queueRegeneration: () => Promise<boolean>;
    onRegenerated: (latest: T) => void;
}

// Regeneration is async on the server (Celery task): POST once, then poll the
// results until `generated_at` advances. A first build runs several LLM
// passes and normally lands within a minute or two, so keep polling for three
// minutes before telling the user to reload instead.
const REGEN_TIMEOUT_MS = 180_000;
const REGEN_POLL_INTERVAL_MS = 2000;

export function useReportRegeneration<T>(options: RegenerationOptions<T>) {
    const { t } = useI18n();
    const regenerating = ref(false);
    const regenStatus = ref<{ theme: "success" | "info" | "error"; text: string } | null>(null);
    let regenStartedAt = 0;
    let regenBaselineAt: string | null = null;

    const { resume: startRegenPoll, pause: stopRegenPoll } = useTimeoutPoll(
        async () => {
            if (Date.now() - regenStartedAt > REGEN_TIMEOUT_MS) {
                stopRegenPoll();
                regenerating.value = false;
                regenStatus.value = { theme: "info", text: t("reflections.campaignReport.regenSlow") };
                return;
            }
            const latest = await options.fetchResults();
            const newGenerated = options.generatedAt(latest);
            if (latest && newGenerated && newGenerated !== regenBaselineAt) {
                stopRegenPoll();
                options.onRegenerated(latest);
                regenerating.value = false;
                regenStatus.value = { theme: "success", text: t("reflections.campaignReport.regenDone", { seconds: ((Date.now() - regenStartedAt) / 1000).toFixed(0) }) };
            }
        },
        REGEN_POLL_INTERVAL_MS,
        { immediate: false },
    );

    onUnmounted(() => stopRegenPoll());

    async function regenerate(currentGeneratedAt: string | null) {
        if (regenerating.value) {
            return;
        }
        regenerating.value = true;
        regenStatus.value = { theme: "info", text: t("reflections.campaignReport.regenerating") };
        regenBaselineAt = currentGeneratedAt;
        if (!(await options.queueRegeneration())) {
            regenerating.value = false;
            regenStatus.value = { theme: "error", text: t("reflections.campaignReport.regenQueueFailed") };
            return;
        }
        regenStartedAt = Date.now();
        startRegenPoll();
    }

    return { regenerating, regenStatus, regenerate };
}
